import React, { useMemo, useState } from 'react';
import { Stethoscope } from 'lucide-react';
import { groupDoctorsByCategory, specialtyVisual } from './specialtyMeta';
import DoctorProfileCard from './DoctorProfileCard';
import DoctorProfileModal from './DoctorProfileModal';
import { IITH, SERIF, SANS } from './iithBrand';

export default function DoctorsDirectory({ doctors, primary, onBookDoctor }) {
  const categories = useMemo(() => groupDoctorsByCategory(doctors), [doctors]);
  const specialties = useMemo(() => {
    const set = new Set(doctors.map((d) => d.specialization).filter(Boolean));
    return Array.from(set).sort();
  }, [doctors]);

  const [category, setCategory] = useState('All');
  const [specialty, setSpecialty] = useState('All');
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    return doctors.filter((d) => {
      if (category !== 'All' && (d.category || 'Doctors') !== category) return false;
      if (specialty !== 'All' && d.specialization !== specialty) return false;
      return true;
    });
  }, [doctors, category, specialty]);

  const grouped = useMemo(() => {
    if (category !== 'All') return [{ category, doctors: filtered }];
    return groupDoctorsByCategory(filtered);
  }, [filtered, category]);

  const book = (d) => onBookDoctor(d.specialization || 'General consultation', d.name);

  if (!doctors.length) {
    return (
      <section id="doctors" className="py-20" style={{ background: IITH.ink }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6 border border-white/10 p-12 text-center" style={{ color: IITH.mute }}>
          <Stethoscope className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Doctors will appear here once published.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="doctors" className="py-20 sm:py-24 border-t border-white/10" style={{ background: IITH.ink }}>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 space-y-8">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: IITH.gold }}>Hospital personnel</p>
          <h2 className="text-3xl sm:text-4xl font-bold mt-2 tracking-tight text-white" style={{ fontFamily: SERIF }}>
            Meet our <span style={{ color: primary || IITH.saffron }}>doctors</span>
          </h2>
          <p className="text-sm mt-3 leading-relaxed" style={{ color: IITH.mute, fontFamily: SANS }}>
            Official IIT Hyderabad Hospital clinicians — filter by team or specialty, open a profile with photograph, then book OPD.
          </p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="doctor-category-filters">
          {['All', ...categories.map((c) => c.category)].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-[12px] border transition ${
                category === c ? 'text-white border-transparent' : 'text-zinc-400 border-white/10 hover:border-white/25'
              }`}
              style={category === c ? { background: primary } : {}}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="doctor-specialty-filters">
          {['All', ...specialties].map((s) => {
            const { Icon } = specialtyVisual(s === 'All' ? '' : s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => setSpecialty(s)}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] border ${
                  specialty === s ? 'bg-white/10 text-zinc-100 border-white/25' : 'text-zinc-500 border-white/8 hover:border-white/20'
                }`}
              >
                {s !== 'All' && <Icon className="h-3 w-3" style={{ color: primary }} />}
                {s === 'All' ? 'All specialties' : s}
              </button>
            );
          })}
        </div>

        {grouped.map(({ category: cat, doctors: list }) => (
          <div key={cat} data-testid={`doctor-group-${cat}`}>
            <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
              {cat}
              <span className="text-[11px] font-normal text-zinc-600">{list.length}</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((d, i) => (
                <DoctorProfileCard
                  key={`${d.name}-${i}`}
                  doctor={d}
                  primary={primary}
                  onView={setSelected}
                  onBook={book}
                  testId={`doctor-card-${cat}-${i}`}
                />
              ))}
            </div>
          </div>
        ))}

        {!filtered.length && (
          <p className="text-sm text-zinc-500 text-center py-8">No doctors match these filters.</p>
        )}
      </div>

      <DoctorProfileModal
        doctor={selected}
        primary={primary}
        onClose={() => setSelected(null)}
        onBook={(d) => { setSelected(null); book(d); }}
      />
    </section>
  );
}
